/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-shadow */
import {
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  InboxOutlined,
  SaveOutlined,
  UploadOutlined,
  RightOutlined,
  EllipsisOutlined
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  Col,
  message,
  Row,
  Spin,
  Tooltip,
  Typography,
  Upload,
  Dropdown,
  Menu,
  Modal,
  Table,
  Tag,
  Space,
  Divider,
  Tabs,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ColumnsType } from 'antd/es/table';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';
import { FineUploaderBasic } from 'fine-uploader/lib/core';
import React, { useMemo, useRef, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import ManagementInfo from 'common/components/ManagementInfo';
import { refreshTokenService } from 'common/services/authenticate';
import { getAccessToken, setAccessToken, setRefreshToken } from 'common/services/common/storage';
import {
  createUseCaseService,
  getUseCaseByIdService,
  getUseCaseDocumentDetailService,
  getUseCaseDocumentEmbeddingService,
  getUseCaseDocumentViewContentService,
  getUseCaseDocumentsService,
  updateUseCaseService,
  deleteUseCaseService,
  deleteUseCaseDocumentsService,
  retrainDocumentService,
} from 'common/services/extends/knowledgeBase';
import { ROUTE_PATHS } from 'common/utils/constant';
import { formatDateTime } from 'common/utils/functions';

/* ===================== TYPES ===================== */

type UseCaseFormTypes = {
  name: string;
  description: string;
};

type UseCasePayload = {
  name: string;
  description: string;
};

type DocumentItem = {
  documentData: {
    id: number;
    code: string;
    name: string;
    extension: string;
    status: number;
    size: string;
    sizeMb: number;
    createdAt: string;
    updatedAt: string;
  };
  updater?: {
    email: string;
    name: string;
  };
  creator?: {
    email: string;
    name: string;
  };
};

/* ===================== VALIDATION ===================== */

const schema = yup.object({
  name: yup
    .string()
    .max(100, 'Max 100 characters')
    .required('This field is required'),
  description: yup
    .string()
    .max(500, 'Max 500 characters')
    .required('This field is required'),
});

/* ===================== DEFAULT ===================== */

const DEFAULT_VALUES: UseCaseFormTypes = {
  name: '',
  description: '',
};

/* ===================== COMPONENT ===================== */

const EditUseCase: React.FC<ActiveRoles> = ({ roleUpdate, roleCreate, roleDelete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const useCaseId = searchParams.get('id');
  const isView = searchParams.get('view') === 'true';
  const queryClient = useQueryClient();
  const token = getAccessToken();

  const isReadOnly = isView || (useCaseId ? !roleUpdate : !roleCreate);

  const method = useForm<UseCaseFormTypes>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  /* ===================== UPLOAD STATE ===================== */

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  // Track file IDs that already showed a specific error (to avoid duplicate toasts)
  const handledErrorIds = useRef<Set<number>>(new Set());

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDoc, setViewerDoc] = useState<DocumentItem | null>(null);
  const [viewerTitle, setViewerTitle] = useState('');
  const [viewerActiveTab, setViewerActiveTab] = useState<'raw' | 'training'>('raw');
  const [viewerRawContent, setViewerRawContent] = useState('');
  const [viewerTrainingContent, setViewerTrainingContent] = useState('');
  const [viewerLoading, setViewerLoading] = useState(false);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalData, setDetailModalData] = useState<any>(null);
  const [detailModalLoading, setDetailModalLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const canSubmit = useMemo(() => !uploading, [uploading]);

  /* ===================== FETCH DETAIL (UPDATE) ===================== */

  const { data: resData, isLoading: detailLoading } = useQuery(
    ['useCase-detail', useCaseId],
    () => getUseCaseByIdService(Number(useCaseId)),
    {
      enabled: !!useCaseId,
      onSuccess: (res) => {
        const data = res?.data;
        if (data?.useCaseData) {
          method.reset({
            name: data.useCaseData.name || '',
            description: data.useCaseData.description || '',
          });
        }
      },
    }
  );

  const detailData = resData?.data;

  /* ===================== FETCH DOCUMENTS (UPDATE) ===================== */

  const { data: docResData, isLoading: docsLoading } = useQuery(
    ['useCase-documents', useCaseId],
    () => getUseCaseDocumentsService(Number(useCaseId)),
    {
      enabled: !!useCaseId,
    }
  );

  const documentItems = useMemo(() => {
    if (docResData?.data) {
      if (Array.isArray(docResData.data)) return docResData.data;
      if (Array.isArray(docResData.data.data)) return docResData.data.data;
      if (Array.isArray(docResData.data.documents)) return docResData.data.documents;
    }
    return detailData?.documents || [];
  }, [docResData, detailData]);

  /* ===================== MUTATIONS ===================== */

  const { mutate: createMutate, isLoading: creating } = useMutation(
    (payload: UseCasePayload) => createUseCaseService(payload),
    {
      onSuccess: () => {
        message.success(t('message.createSuccess'));
        navigate(ROUTE_PATHS.KNOWLEDGE_BASE_MANAGEMENT);
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors && errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            if (error.code === 'unique') {
              method.setError('name' as keyof UseCaseFormTypes, {
                message: 'Use case name already exists.',
              });
            } else {
              method.setError(error.field as keyof UseCaseFormTypes, {
                message: error.message || '',
              });
            }
          });
        } else {
          message.error(t('message.createError'));
        }
      },
    }
  );

  const { mutate: updateMutate, isLoading: updating } = useMutation(
    (payload: UseCasePayload) => updateUseCaseService(Number(useCaseId), payload),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors && errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            if (error.code === 'unique') {
              method.setError('name' as keyof UseCaseFormTypes, {
                message: 'Use case name already exists.',
              });
            } else {
              method.setError(error.field as keyof UseCaseFormTypes, {
                message: error.message || '',
              });
            }
          });
        } else {
          message.error(t('message.updateError'));
        }
      },
    }
  );

  const { mutate: deleteMutate, isLoading: deleting } = useMutation(
    (ids: number[]) => deleteUseCaseService({ ids }),
    {
      onSuccess: () => {
        message.success('Deleted successfully');
        navigate(ROUTE_PATHS.KNOWLEDGE_BASE_MANAGEMENT);
      },
      onError: () => {
        message.error('Failed to delete');
      },
    }
  );

  const { mutate: deleteDocumentsMutate, isLoading: deletingDocs } = useMutation(
    (ids: number[]) => deleteUseCaseDocumentsService(Number(useCaseId), { ids }),
    {
      onSuccess: () => {
        message.success(t('knowledge.useCase.deleteDocSuccess', 'Documents deleted successfully'));
        setSelectedRowKeys([]);
        queryClient.invalidateQueries(['useCase-detail', useCaseId]);
        queryClient.invalidateQueries(['useCase-documents', useCaseId]);
      },
      onError: () => {
        message.error(t('knowledge.useCase.deleteDocFailed', 'Failed to delete documents'));
      },
    }
  );

  const { mutate: retrainDocumentMutate, isLoading: retraining } = useMutation(
    (documentId: number) => retrainDocumentService(Number(useCaseId), documentId),
    {
      onSuccess: () => {
        message.success('Retrain started successfully');
        queryClient.invalidateQueries(['useCase-documents', useCaseId]);
      },
      onError: () => {
        message.error('Failed to retrain document');
      },
    }
  );

  /* ===================== SUBMIT ===================== */

  const onSubmit = async () => {
    const valid = await method.trigger();
    if (!valid) return;

    if (uploading) {
      message.warning(t('message.uploadInProgress'));
      return;
    }

    const values = method.getValues();

    const payload: UseCasePayload = {
      name: values.name.trim(),
      description: values.description?.trim() || '',
    };

    if (useCaseId) {
      updateMutate(payload);
    } else {
      createMutate(payload);
    }
  };

  /* ===================== UPLOAD ===================== */

  const uploader = useMemo(() => new FineUploaderBasic({
    autoUpload: false,
    request: {
      endpoint: `${process.env.REACT_APP_API_BASE_URL}knowledge-use-cases/${useCaseId}/documents/upload-chunk`,
      customHeaders: {
        Authorization: `Bearer ${token}`,
      },
    },
    chunking: {
      enabled: true,
      mandatory: true,
      partSize: 5 * 1024 * 1024,
      success: {
        jsonPayload: true,
        endpoint: `${process.env.REACT_APP_API_BASE_URL}knowledge-use-cases/${useCaseId}/documents/merge-chunk`,
        params(id: number) {
          const uuid = uploader.getUuid(id);
          const fileName = (uploader.getFile(id) as File).name;
          return {
            chunkedUuid: uuid,
            qqfilename: fileName,
          };
        }
      },
    },
    callbacks: {
      onComplete(id: number, name: string, res: any, xhr: XMLHttpRequest) {
        if (res?.success || (xhr.status >= 200 && xhr.status < 300)) {
          setFileList((prev) => prev.filter((f) => f.name !== name));
          queryClient.invalidateQueries(['useCase-detail', useCaseId]);
          queryClient.invalidateQueries(['useCase-documents', useCaseId]);
        } else {
          // Handle specific error codes from server response
          const errors = res?.errors || [];
          const hasFilenameExisted = errors.some(
            (err: any) => err.code === 'filenameExisted'
          );
          if (hasFilenameExisted) {
            message.error('File has been existed');
            handledErrorIds.current.add(id);
          }
        }
      },
      onAllComplete: (successful: number[], failed: number[]) => {
        if (successful && successful.length > 0) {
          message.success('File uploaded successfully');
        }
        // Only show generic error if there are failed files not already handled
        const unhandledFailed = (failed || []).filter(
          (id) => !handledErrorIds.current.has(id)
        );
        if (unhandledFailed.length > 0) {
          message.error('File uploaded failed');
        }
        // Always close modal and reset state
        setUploadModalOpen(false);
        setFileList([]);
        setFileErrors({});
        handledErrorIds.current.clear();
        setUploading(false);
      },
      onError: async (id: number, name: string, errorReason: string, xhr: XMLHttpRequest) => {
        if (xhr && xhr.status === 401) {
          await refreshTokenService().then((data) => {
            setAccessToken(data.accessToken);
            setRefreshToken(data.refreshToken);
            uploader.setCustomHeaders({ Authorization: `Bearer ${data.accessToken}` });
            uploader.retry(id);
          });
        }
      }
    }
  }), [useCaseId, token, queryClient, t]);

  const ACCEPTED_EXTENSIONS = ['epub', 'rtf', 'txt', 'docx', 'doc', 'odt', 'pdf', 'pptx', 'odp', 'md', 'ppt'];
  const MAX_FILE_SIZE_MB = 100;

  const handleBeforeUpload = (file: RcFile) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';

    // Check unsupported format → reject
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      message.error(`Unsupported file format: "${file.name}"`);
      return Upload.LIST_IGNORE;
    }

    // Check duplicate by name → warning + reject
    const isDuplicate = fileList.some((f) => f.name === file.name);
    if (isDuplicate) {
      message.warning(`"${file.name}" already added`);
      return Upload.LIST_IGNORE;
    }

    // Check size → add to list with error state, tooltip will show on hover
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_FILE_SIZE_MB) {
      setFileErrors((prev) => ({
        ...prev,
        [file.uid]: 'File exceeds maximum size',
      }));
      return false;
    }

    return false;
  };

  const handleUploadChange = (info: { fileList: UploadFile[] }) => {
    setFileList(info.fileList);
  };

  const handleUpload = () => {
    // Only upload valid files (exclude files with errors)
    const validFiles = fileList.filter((file) => !fileErrors[file.uid]);
    if (validFiles.length === 0) {
      message.warning('No valid files to upload!');
      return;
    }
    setUploading(true);
    uploader.addFiles(validFiles.map((file) => (file as any).originFileObj));
    uploader.uploadStoredFiles();
  };

  /* ===================== DUPLICATE CONFIRM (READY) ===================== */

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDuplicateConfirm = (useCaseName: string, file: UploadFile) => {
    Modal.confirm({
      title: t('message.duplicateFileTitle'),
      content: t('message.duplicateFileContent', { name: useCaseName }),
      okText: t('system.continue'),
      cancelText: t('system.cancel'),
      onOk: () => {
        setFileList((prev) => [...prev, file]);
      },
    });
  };

  /* ===================== VIEWERS & TABLE ===================== */

  const openDetailViewer = React.useCallback(async (doc: DocumentItem) => {
    setDetailModalOpen(true);
    setDetailModalLoading(true);
    setDetailModalData(null);
    try {
      if (!useCaseId || !doc.documentData?.id) {
        throw new Error('Missing useCaseId or documentId');
      }
      const res = await getUseCaseDocumentDetailService(Number(useCaseId), doc.documentData.id);
      setDetailModalData(res?.data || doc.documentData);
    } catch {
      message.error(t('message.loadDetailFailed'));
      setDetailModalOpen(false);
    } finally {
      setDetailModalLoading(false);
    }
  }, [useCaseId, t]);

  const openViewer = React.useCallback(async (doc: DocumentItem, tab: 'raw' | 'training') => {
    setViewerOpen(true);
    setViewerDoc(doc);
    setViewerTitle(doc.documentData?.name || '');
    setViewerActiveTab(tab);
    setViewerLoading(true);

    try {
      if (!useCaseId || !doc.documentData?.id) {
        throw new Error('Missing useCaseId or documentId');
      }

      const [rawRes, trainingRes] = await Promise.all([
        getUseCaseDocumentViewContentService(Number(useCaseId), doc.documentData.id)
          .catch(() => null),
        getUseCaseDocumentEmbeddingService(Number(useCaseId), doc.documentData.id)
          .catch(() => null)
      ]);

      const parseContent = (response: any) => {
        if (!response) return '';
        const payloadData = response.data?.data || response.data;
        let documentsArr = [];
        if (Array.isArray(payloadData?.documents)) {
          documentsArr = payloadData.documents;
        } else if (Array.isArray(payloadData)) {
          documentsArr = payloadData;
        }

        if (documentsArr.length > 0) {
          return documentsArr
            .map((item: any) => item?.content || item?.text || item?.pageContent || '')
            .filter(Boolean)
            .join('\n\n----------\n\n');
        }
        if (typeof response.data === 'string') return response.data;
        if (response.data?.content) return response.data.content;
        return JSON.stringify(response.data || response, null, 2);
      };

      setViewerRawContent(parseContent(rawRes));
      setViewerTrainingContent(parseContent(trainingRes));
    } catch {
      message.error(t('message.loadFileContentFailed'));
      setViewerOpen(false);
    } finally {
      setViewerLoading(false);
    }
  }, [useCaseId, t]);

  const columns: ColumnsType<DocumentItem> = useMemo(() => [
    {
      title: t('knowledge.document.name'),
      dataIndex: ['documentData', 'name'],
    },
    {
      title: t('knowledge.document.uploader'),
      key: 'uploader',
      render: (_, record) => record.creator?.name || '-',
      width: 160,
    },
    {
      title: t('knowledge.document.status'),
      dataIndex: ['documentData', 'status'],
      width: 140,
      render: (status) => {
        const statusMap: Record<number, { color: string; label: string }> = {
          1: { color: 'success', label: 'Complete' },
          2: { color: 'orange', label: 'Processing' },
          3: { color: 'error', label: 'Error' },
        };
        const cfg = statusMap[status] ?? { color: 'default', label: '-' };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: t('knowledge.document.createdAt'),
      key: 'createdAt',
      render: (_, record) => formatDateTime(record.documentData?.createdAt),
      width: 180,
    },
    {
      title: '',
      width: 60,
      align: 'center',
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="detail"
              onClick={() => openViewer(record, 'raw')}
            >
              {t('knowledge.useCase.viewFileContent')}
            </Menu.Item>

            <Menu.Item
              key="content"
              disabled={record.documentData?.status !== 2}
              onClick={() => openViewer(record, 'training')}
            >
              {t('knowledge.useCase.viewTrainingContent')}
            </Menu.Item>
            {roleDelete && (
              <Menu.Item
                key="delete"
                danger
                onClick={() => {
                  Modal.confirm({
                    title: 'Delete Document',
                    content: 'Are you sure you want to delete this document?',
                    onOk: () => {
                      if (record.documentData?.id) {
                        deleteDocumentsMutate([record.documentData.id]);
                      }
                    },
                  });
                }}
              >
                {t('system.delete', 'Delete')}
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ], [t, openViewer, roleDelete, deleteDocumentsMutate]);

  const viewColumns: ColumnsType<DocumentItem> = useMemo(() => [
    {
      title: 'File Name',
      dataIndex: ['documentData', 'name'],
    },
    {
      title: 'Status',
      dataIndex: ['documentData', 'status'],
      width: 140,
      render: (status) => {
        const statusMap: Record<number, { color: string; label: string }> = {
          1: { color: 'success', label: 'Complete' },
          2: { color: 'orange', label: 'Processing' },
          3: { color: 'error', label: 'Error' },
        };
        const cfg = statusMap[status] ?? { color: 'default', label: '-' };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Size',
      dataIndex: ['documentData', 'sizeMb'],
      width: 140,
      render: (sizeMb, record) => {
        if (!sizeMb) {
          const sizeKb = record.documentData?.size;
          const sizeKbNum = sizeKb != null ? Number(sizeKb) : null;
          return (
            <Typography.Text>
              {sizeKbNum != null && !Number.isNaN(sizeKbNum)
                ? `${sizeKbNum.toLocaleString('de-DE')} KB`
                : '-'}
            </Typography.Text>
          );
        }
        return (
          <Typography.Text>
            {sizeMb.toFixed(2)}
            {' '}
            MB
          </Typography.Text>
        );
      },
    },
    {
      title: 'Uploaded At',
      key: 'createdAt',
      render: (_, record) => formatDateTime(record.documentData?.createdAt),
      width: 180,
    },
    {
      title: '',
      width: 60,
      align: 'center',
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item key="detail" onClick={() => openViewer(record, 'raw')}>
              {t('knowledge.useCase.viewFileContent')}
            </Menu.Item>
            <Menu.Item
              key="content"
              // disabled={record.documentData?.status !== 2}
              onClick={() => openViewer(record, 'training')}
            >
              {t('knowledge.useCase.viewTrainingContent')}
            </Menu.Item>
            {record.documentData?.status === 3 && (
              <Menu.Item
                key="retrain"
                onClick={() => {
                  if (record.documentData?.id) {
                    retrainDocumentMutate(record.documentData.id);
                  }
                }}
              >
                Retrain
              </Menu.Item>
            )}
            {roleDelete && (
              <Menu.Item
                key="delete"
                danger
                onClick={() => {
                  Modal.confirm({
                    title: 'Delete Document',
                    content: 'Are you sure you want to delete this document?',
                    onOk: () => {
                      if (record.documentData?.id) {
                        deleteDocumentsMutate([record.documentData.id]);
                      }
                    },
                  });
                }}
              >
                {t('role.destroy', 'Delete')}
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ], [t, openViewer, roleDelete, deleteDocumentsMutate, retrainDocumentMutate]);

  let pageTitle: React.ReactNode = (
    <Space style={{ fontSize: '18px', fontWeight: 500, columnGap: 16 }}>
      <Typography.Text
        style={{ cursor: 'pointer', fontSize: '16px', color: '#8c8c8c' }}
        onClick={() => navigate(ROUTE_PATHS.KNOWLEDGE_BASE_MANAGEMENT)}
      >
        Knowledge Base
      </Typography.Text>
      <RightOutlined style={{ fontSize: '14px', color: '#8c8c8c' }} />
      <Typography.Text style={{ fontSize: '18px', fontWeight: 600 }}>
        Create Use Case
      </Typography.Text>
    </Space>
  );

  if (useCaseId) {
    if (isView) {
      pageTitle = (
        <Space style={{ fontSize: '18px', fontWeight: 500, columnGap: 16 }}>
          <Typography.Text
            style={{ cursor: 'pointer', fontSize: '16px', color: '#8c8c8c' }}
            onClick={() => navigate(ROUTE_PATHS.KNOWLEDGE_BASE_MANAGEMENT)}
          >
            Knowledge Base
          </Typography.Text>
          <RightOutlined style={{ fontSize: '14px', color: '#8c8c8c' }} />
          <Typography.Text style={{ fontSize: '18px', fontWeight: 600 }}>
            {resData?.data?.useCaseData?.name || ''}
          </Typography.Text>
        </Space>
      );
    } else {
      pageTitle = (
        <Space style={{ fontSize: '18px', fontWeight: 500, columnGap: 16 }}>
          <Typography.Text
            style={{ cursor: 'pointer', fontSize: '16px', color: '#8c8c8c' }}
            onClick={() => navigate(ROUTE_PATHS.KNOWLEDGE_BASE_MANAGEMENT)}
          >
            Knowledge Base
          </Typography.Text>
          <RightOutlined style={{ fontSize: '14px', color: '#8c8c8c' }} />
          <Typography.Text
            style={{ cursor: 'pointer', fontSize: '16px', color: '#8c8c8c' }}
            onClick={() => navigate(`${ROUTE_PATHS.KNOWLEDGE_BASE_DETAIL}?id=${useCaseId}&view=true`)}
          >
            {resData?.data?.useCaseData?.name || ''}
          </Typography.Text>
          <RightOutlined style={{ fontSize: '14px', color: '#8c8c8c' }} />
          <Typography.Text style={{ fontSize: '18px', fontWeight: 600 }}>
            Edit
          </Typography.Text>
        </Space>
      );
    }
  }

  /* ===================== RENDER ===================== */

  return (
    <FormProvider {...method}>
      <HeaderPage
        fixed
        title={pageTitle}
      />

      <Spin spinning={detailLoading || creating || updating || docsLoading || deleting}>
        <div className="t-mainlayout_wrapper">
          {isView ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <Space align="center" size="middle">
                      <Typography.Title level={4} style={{ margin: 0 }}>
                        {resData?.data?.useCaseData?.name}
                      </Typography.Title>
                      <Tag
                        color={resData?.data?.useCaseData?.status === 2 ? '#e6f4ea' : 'default'}
                        style={{
                          color: resData?.data?.useCaseData?.status === 2 ? '#1e8e3e' : '#5f6368',
                          borderRadius: '12px',
                          padding: '0 12px',
                          fontWeight: 500,
                          border: 'none'
                        }}
                      >
                        {resData?.data?.useCaseData?.status === 2 ? 'Active' : 'Inactive'}
                      </Tag>
                    </Space>
                    <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
                      {resData?.data?.useCaseData?.description || 'No description provided.'}
                    </Typography.Text>
                  </div>
                  <Space>
                    <Tooltip
                      title={resData?.data?.useCaseData?.status !== 2 ? 'You need active usecase to upload files' : undefined}
                    >
                      <Button
                        icon={<CloudUploadOutlined />}
                        onClick={() => setUploadModalOpen(true)}
                        disabled={resData?.data?.useCaseData?.status !== 2}
                      >
                        Upload
                      </Button>
                    </Tooltip>
                    {roleUpdate && (
                      <Button onClick={() => navigate(`${ROUTE_PATHS.KNOWLEDGE_BASE_DETAIL}?id=${useCaseId}`)}>
                        <EditOutlined />
                        {' '}
                        Edit
                      </Button>
                    )}
                    {roleDelete && (
                      <Button
                        danger
                        onClick={() => {
                          Modal.confirm({
                            title: 'Confirm delete this use case?',
                            onOk: () => {
                              deleteMutate([Number(useCaseId)]);
                            },
                          });
                        }}
                      >
                        <DeleteOutlined />
                        {' '}
                        Delete
                      </Button>
                    )}
                  </Space>
                </div>

                <Divider style={{ margin: '24px 0 16px' }} />

                <Row gutter={[24, 16]}>
                  <Col span={8}>

                    <div style={{ display: 'flex', marginBottom: 12 }}>
                      <div style={{ width: 120 }}><Typography.Text type="secondary">Total Files</Typography.Text></div>
                      <div>
                        <Typography.Text strong>
                          {resData?.data?.useCaseData?.ttlFile || 0}
                        </Typography.Text>
                      </div>
                    </div>
                    <div style={{ display: 'flex' }}>
                      <div style={{ width: 120 }}><Typography.Text type="secondary">Last Updated</Typography.Text></div>
                      <div>
                        <Typography.Text strong>
                          {formatDateTime(resData?.data?.useCaseData?.updatedAt)}
                          {' '}
                          by
                          {' '}
                          {resData?.data?.updater?.name || '-'}
                        </Typography.Text>
                      </div>
                    </div>
                  </Col>

                  <Col span={8}>
                    <div style={{ display: 'flex' }}>
                      <div style={{ width: 140 }}><Typography.Text type="secondary">Total Size</Typography.Text></div>
                      <div>
                        <Typography.Text strong>
                          {(resData?.data?.useCaseData?.ttlSize || 0).toFixed(2)}
                          {' '}
                          MB
                        </Typography.Text>
                      </div>
                    </div>
                  </Col>

                  <Col span={8}>
                    <div style={{ display: 'flex' }}>
                      <div style={{ width: 100 }}><Typography.Text type="secondary">Created</Typography.Text></div>
                      <div>
                        <Typography.Text strong>
                          {formatDateTime(resData?.data?.useCaseData?.createdAt)}
                          {' '}
                          by
                          {' '}
                          {resData?.data?.creator?.name || '-'}
                        </Typography.Text>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card>

              <Card
                className="u-mt-0"
                title={(
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>Documents</Typography.Title>
                    {selectedRowKeys.length > 0 && roleDelete && (
                      <Button
                        danger
                        type="primary"
                        icon={<DeleteOutlined />}
                        loading={deletingDocs}
                        onClick={() => {
                          Modal.confirm({
                            title: 'Delete Documents',
                            content: `Are you sure you want to delete ${selectedRowKeys.length} document(s)?`,
                            onOk: () => deleteDocumentsMutate(selectedRowKeys as number[]),
                          });
                        }}
                      >
                        {`Delete (${selectedRowKeys.length})`}
                      </Button>
                    )}
                  </div>
                )}
              >
                <Table
                  rowKey={(r) => r.documentData?.id || r.id}
                  rowSelection={{
                    selectedRowKeys,
                    onChange: setSelectedRowKeys,
                  }}
                  columns={viewColumns as any}
                  dataSource={documentItems}
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                paddingBottom: 64,
              }}
            >
              <div style={{ width: '100%', maxWidth: '800px', margin: '24px auto 0' }}>
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {useCaseId ? 'Edit Use Case' : 'Create Use Case'}
                </Typography.Title>
                <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, marginBottom: 24 }}>
                  {useCaseId
                    ? 'Update the name and description for your knowledge base.'
                    : 'Provide the name and description for your knowledge base.'}
                </Typography.Text>

                <Card>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    Use Case Details
                  </Typography.Title>
                  <Typography.Text
                    type="secondary"
                    style={{
                      display: 'block',
                      marginTop: 4,
                      marginBottom: 24,
                      fontSize: '13px',
                    }}
                  >
                    Only the name and description can be updated.
                  </Typography.Text>

                  <Row gutter={16}>
                    <Col span={24} className="u-mb-16">
                      <Typography.Text strong style={{ fontSize: '14px', marginBottom: 8, display: 'block' }}>
                        Use Case Name
                      </Typography.Text>
                      <Controller
                        name="name"
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            size="large"
                            style={{ backgroundColor: '#fafafa' }}
                            error={fieldState.error?.message}
                            disabled={isReadOnly}
                          />
                        )}
                      />
                    </Col>

                    <Col span={24} className="u-mb-16">
                      <Typography.Text strong style={{ fontSize: '14px', marginBottom: 8, display: 'block' }}>
                        Description
                      </Typography.Text>
                      <Controller
                        name="description"
                        render={({ field, fieldState }) => (
                          <TextArea
                            {...field}
                            rows={4}
                            style={{ backgroundColor: '#fafafa' }}
                            status={fieldState.error ? 'error' : ''}
                            disabled={isReadOnly}
                          />
                        )}
                      />
                    </Col>

                    <Col span={24}>
                      <Button
                        type="primary"
                        onClick={method.handleSubmit(onSubmit)}
                        loading={creating || updating}
                        disabled={!canSubmit}
                      >
                        {useCaseId ? 'Save Changes' : t('system.create')}
                      </Button>
                    </Col>
                  </Row>
                </Card>
              </div>
            </div>
          )}
        </div>
      </Spin>

      <Modal
        open={uploadModalOpen}
        title="Upload Documents"
        onCancel={() => setUploadModalOpen(false)}
        footer={null}
      >
        <Spin spinning={uploading} tip="Uploading...">
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Upload.Dragger
              multiple
              fileList={fileList}
              onChange={handleUploadChange}
              beforeUpload={handleBeforeUpload}
              showUploadList={false}
              className="u-mt-8"
              style={{ width: '100%' }}
              accept=".epub,.rtf,.txt,.docx,.doc,.odt,.pdf,.pptx,.odp,.md,.ppt"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Supported: EPUB, RTF, TXT, DOCX, DOC, ODT, PDF, PPTX, ODP, MD, PPT &bull; Max 100 MB
              </p>
            </Upload.Dragger>
            {fileList?.length > 0 && (
              <div className="u-mt-8">
                <Typography.Title level={5}>Selected files</Typography.Title>
                {fileList.map((file: any, idx) => {
                  const errorMsg = fileErrors[file.uid];
                  return (
                    <Tooltip
                      key={`${file.uid}-${file.name}-${idx.toString()}`}
                      title={errorMsg || undefined}
                    >
                      <div
                        className="u-mt-8"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          color: errorMsg ? '#ff4d4f' : '#1d8daa',
                          width: 'fit-content',
                        }}
                      >
                        <span>{file.name}</span>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            )}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <Button
                type="primary"
                onClick={handleUpload}
                loading={uploading}
                className="u-mt-8"
                disabled={uploading || fileList.filter((f) => !fileErrors[f.uid]).length === 0}
              >
                Upload Selected Files
              </Button>
              <Button
                type="dashed"
                onClick={() => { setFileList([]); setFileErrors({}); }}
                className="u-mt-8"
                disabled={uploading}
                style={{ marginLeft: '16px' }}
              >
                Clear
              </Button>
            </div>
          </div>
        </Spin>
      </Modal>

      {/* ===================== VIEWER MODAL ===================== */}

      <Modal
        open={viewerOpen}
        title={viewerTitle}
        footer={null}
        width={800}
        onCancel={() => setViewerOpen(false)}
      >
        <Spin spinning={viewerLoading}>
          <Tabs
            activeKey={viewerActiveTab}
            onChange={(key) => setViewerActiveTab(key as 'raw' | 'training')}
            items={[
              {
                key: 'raw',
                label: 'Raw Content',
                children: (
                  <pre
                    style={{
                      maxHeight: 500,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      backgroundColor: '#fafafa',
                      padding: 16,
                      borderRadius: 8
                    }}
                  >
                    {viewerRawContent || 'No content available.'}
                  </pre>
                ),
              },
              {
                key: 'training',
                label: 'Training Content',
                children: (
                  <pre
                    style={{
                      maxHeight: 500,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      backgroundColor: '#fafafa',
                      padding: 16,
                      borderRadius: 8
                    }}
                  >
                    {viewerTrainingContent || 'No content available.'}
                  </pre>
                ),
              },
            ]}
          />
        </Spin>
      </Modal>

      {/* ===================== DETAIL MODAL ===================== */}

      <Modal
        open={detailModalOpen}
        title={t('knowledge.useCase.documentDetail')}
        footer={null}
        onCancel={() => setDetailModalOpen(false)}
      >
        <Spin spinning={detailModalLoading}>
          {detailModalData && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Typography.Text strong>
                    {t('knowledge.document.name')}
                    :
                  </Typography.Text>
                </Col>
                <Col span={16}>
                  <Typography.Text>
                    {detailModalData.documentData?.name || detailModalData.name}
                  </Typography.Text>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Typography.Text strong>
                    {t('knowledge.document.extension', 'Extension')}
                    :
                  </Typography.Text>
                </Col>
                <Col span={16}>
                  <Typography.Text>
                    {detailModalData.documentData?.extension || detailModalData.extension || '-'}
                  </Typography.Text>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Typography.Text strong>
                    {t('knowledge.document.size', 'Size')}
                    :
                  </Typography.Text>
                </Col>
                <Col span={16}>
                  <Typography.Text>
                    {detailModalData.documentData?.sizeMb || detailModalData.sizeMb || 0}
                    {' '}
                    MB
                  </Typography.Text>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Typography.Text strong>
                    {t('knowledge.document.status')}
                    :
                  </Typography.Text>
                </Col>
                <Col span={16}>
                  {(() => {
                    const statusMap: Record<number, { color: string; label: string }> = {
                      1: { color: 'success', label: 'Complete' },
                      2: { color: 'orange', label: 'Processing' },
                      3: { color: 'error', label: 'Error' },
                    };
                    const s = detailModalData.documentData?.status ?? detailModalData.status;
                    const cfg = statusMap[s] ?? { color: 'default', label: '-' };
                    return <Tag color={cfg.color}>{cfg.label}</Tag>;
                  })()}
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Typography.Text strong>
                    {t('knowledge.document.createdAt')}
                    :
                  </Typography.Text>
                </Col>
                <Col span={16}>
                  <Typography.Text>
                    {formatDateTime(
                      detailModalData.documentData?.createdAt || detailModalData.createdAt
                    )}
                  </Typography.Text>
                </Col>
              </Row>
            </div>
          )}
        </Spin>
      </Modal>
    </FormProvider>
  );
};

export default EditUseCase;
